import type { I_LoyaltyProgramConfig } from '@enable-tech/shared-types/modules/loyaltyProgram/common/backend/responses/schemas';
import { COUNTRIES_DATA } from '@fe/shared/Countries';
import type { E_CountryDialCode } from '@fe/types/modules/localization/common/backend/enums/';
import type { T_CountryItem } from '@fe/types/modules/localization/common/client/types';
import { useContext, useEffect, useState } from 'preact/hooks';

import { CountryCodeSelectorContext } from '@/context/CountryCodeSelectorContext';
import { useLoyaltyProgramContext } from '@/context/LoyaltyProgramContext';
import { usePlatformHandlers } from '@/context/PlatformContext';
import type { T_EnableLoyaltyProgram } from '@/types/Shopify';

export interface I_UseShopLoyaltyConfigReturn {
  loyaltyPrograms: T_EnableLoyaltyProgram[] | undefined;
  loyaltyConfig: I_LoyaltyProgramConfig | null;
  brandId: string;
  loading: boolean;
}

export function useShopLoyaltyConfig(): I_UseShopLoyaltyConfigReturn {
  const { getShopLoyaltyConfig } = usePlatformHandlers();
  const { setSelectedCountryCode, setSelectedCountry } = useContext(CountryCodeSelectorContext);
  const { setBrandData } = useLoyaltyProgramContext();

  const [loyaltyPrograms, setLoyaltyPrograms] = useState<T_EnableLoyaltyProgram[]>();
  const [loyaltyConfig, setLoyaltyConfig] = useState<I_LoyaltyProgramConfig | null>(null);
  const [brandId, setBrandId] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    async function fetchShopLoyaltyConfig() {
      try {
        const data = await getShopLoyaltyConfig?.();
        if (!isMounted) return;
        if (data?.countryAcronym) {
          const country = COUNTRIES_DATA.find(
            (c) => c.isoCode.toLowerCase() === data?.countryAcronym?.toLowerCase(),
          ) as T_CountryItem | undefined;
          if (country) {
            setSelectedCountryCode(country.dialCode as E_CountryDialCode);
            setSelectedCountry(country);
          }
        }
        if (data?.loyaltyPrograms) setLoyaltyPrograms(data.loyaltyPrograms);
        if (data?.loyaltyConfig) setLoyaltyConfig(data.loyaltyConfig as I_LoyaltyProgramConfig);
        if (data?.brands && data?.brands?.length > 0) {
          const brand = data.brands[0] as { _id?: string; id?: string };
          setBrandId(brand._id || brand.id || '');
          setBrandData(data.brands[0]);
        }
      } catch (err) {
        console.error('Error fetching loyalty data:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    void fetchShopLoyaltyConfig();
    return () => {
      isMounted = false;
    };
  }, []);

  return { loyaltyPrograms, loyaltyConfig, brandId, loading };
}
